import React, { useState } from "react";
import { Loader, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { login, clearError } from "../../store/authSlice";
import SupportyLogo from "../../assets/auth/supporty-logo.svg";
import LoginSideImage from "../../assets/auth/sign-in-bg.svg";
import AuthLayout from "./AuthLayout";
import { getMessaging, getToken } from "firebase/messaging";
import { app, db } from "../../config/firebase";
import { doc, setDoc } from "firebase/firestore";

interface FormErrors {
  email?: any;
  password?: any;
  general?: any;
}

export function LoginForm(): JSX.Element {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [showDeactivatedPopup, setShowDeactivatedPopup] = useState(false);
  const [otpChoiceModal, setOtpChoiceModal] = useState<{
    open: boolean;
    phone?: string;
    email?: string;
    options?: { email?: boolean; phoneNumber?: string };
  }>({ open: false });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Fix: Check if auth state exists and provide default values if not
  const authState = useAppSelector((state) => state.auth);
  const fetchError = authState?.fetchError || null;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  console.log("errrr", errors);
  console.log(fetchError, "fetchError");
  const handleEmailBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    if (!formData.email) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
    } else if (!validateEmail(formData.email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, email: value }));

    // Clear any Redux auth errors when user starts typing
    if (fetchError) {
      dispatch(clearError());
    }

    // Only show validation errors after field has been touched
    if (touched.email) {
      if (!value) {
        setErrors((prev) => ({ ...prev, email: "Email is required" }));
      } else if (!validateEmail(value)) {
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address",
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    }
  };

  const handleFocus = (field: keyof FormErrors) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      delete newErrors.general; // Also clear general errors on focus
      return newErrors;
    });
    // Clear any Redux auth errors when user starts typing
    if (fetchError) {
      dispatch(clearError());
    }
  };

  const handleChange = (field: keyof FormErrors, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      delete newErrors.general; // Also clear general errors on input
      return newErrors;
    });
    // Clear any Redux auth errors when user starts typing
    if (fetchError) {
      dispatch(clearError());
    }
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Handle cases where phoneNumber might not be a string or might be falsy
    if (!phoneNumber || typeof phoneNumber !== "string") {
      return "";
    }

    let formattedNumber = phoneNumber.toString().trim();
    if (!formattedNumber.startsWith("+")) {
      formattedNumber = "+" + formattedNumber;
    }
    return formattedNumber;
  };

  const handleSendOtp = async (phone: string, email: string, type: string) => {
    if (!phone && type === "SMS") {
      setErrors((prev) => ({
        ...prev,
        general: "Please enter a valid phone number.",
      }));
      return;
    }
    // Only format if phone is provided and not empty
    const formattedPhone = phone ? formatPhoneNumber(phone) : "";
    console.log(formattedPhone,'formattedPhone');

    try {
      const response = await fetch(
        "https://us-central1-supporty-staging.cloudfunctions.net/sendOTP",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phNumber: formattedPhone, type, email }),
        }
      );

      const result = await response.json();
      if (result.success) {
        sessionStorage.setItem("verificationPhone", formattedPhone);
        navigate("/signup/otp-verify", {
          state: { phoneNumber: formattedPhone, type, email },
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          general: result.error || "Failed to send verification code.",
        }));
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        general: "An unexpected error occurred.",
      }));
    }
  };
  // Function to request and store FCM token
  const storeFCMToken = async (userId: string) => {
    try {
      console.log("Requesting FCM token for user:", userId);

      // Request notification permission with better user experience
      const permissionResult = await requestNotificationPermission();
      console.log("Notification permission result:", permissionResult);

      if (permissionResult !== "granted") {
        console.log("Notification permission not granted");
        return null;
      } // Initialize Firebase Messaging
      const messaging = getMessaging(app);

      // Verify that the messaging service is initialized correctly
      if (!messaging) {
        console.error("Firebase Messaging failed to initialize properly");
        return null;
      }

      // VAPID key from Firebase Console (Web Push certificates)
      // Make sure this key is correct and matches the one in Firebase Console
      const vapidKey =
        "BNWMA2MBanGpCS3zQJqh4meZHjBPBOJjYjAwd1hg2JRMiVYIoERSrCj-h0h9L5ytlDL0r3j6FGENAOdvBcnHUAo";
      // Register service worker if needed (for browser context)
      let serviceWorkerRegistration;
      if ("serviceWorker" in navigator) {
        try {
          // First check if the service worker is already registered
          serviceWorkerRegistration =
            await navigator.serviceWorker.getRegistration(
              "/firebase-messaging-sw.js"
            );

          // If not registered, register it
          if (!serviceWorkerRegistration) {
            console.log("Service Worker not found, registering now...");
            serviceWorkerRegistration = await navigator.serviceWorker.register(
              "/firebase-messaging-sw.js",
              {
                scope: "/",
              }
            );
          }

          // Wait for the service worker to be ready
          await navigator.serviceWorker.ready;

          console.log(
            "Service Worker registered with scope:",
            serviceWorkerRegistration.scope
          );
        } catch (error) {
          console.error("Service Worker registration failed:", error);
        }
      } // Get token
      let fcmToken;
      try {
        console.log(
          "Getting FCM token with vapidKey:",
          vapidKey.substring(0, 10) + "..."
        );
        console.log(
          "Service worker registration scope:",
          serviceWorkerRegistration?.scope
        );

        fcmToken = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration,
        });

        console.log("FCM Token successfully retrieved");
      } catch (tokenError) {
        console.error("Error getting FCM token:", tokenError);
        // Fallback: try without service worker registration
        try {
          console.log(
            "Trying to get token without service worker registration"
          );
          fcmToken = await getToken(messaging, { vapidKey });
        } catch (fallbackError) {
          console.error("Fallback token retrieval also failed:", fallbackError);
          throw tokenError; // Throw original error for better diagnosis
        }
      }

      if (fcmToken) {
        console.log("FCM Token:", fcmToken.substring(0, 10) + "...");

        // Store token in Firestore
        const tokenRef = doc(db, `deviceTokens/${userId}`);
        await setDoc(
          tokenRef,
          {
            token: fcmToken,
            userId: userId,
            device: "web",
            platform: navigator.platform,
            browser: navigator.userAgent,
            lastUpdated: new Date().toISOString(),
          },
          { merge: true }
        );

        console.log("FCM token saved to Firestore");
        return fcmToken;
      } else {
        console.error("No FCM token received");
        return null;
      }
    } catch (error) {
      console.error("Error generating/storing FCM token:", error);
      return null;
    }
  };

  // Helper function to request notification permission with better user experience
  const requestNotificationPermission = async () => {
    try {
      // Check if browser supports notifications
      if (!("Notification" in window)) {
        console.log("This browser does not support notifications");
        return null;
      }

      // Check if permission is already granted
      if (Notification.permission === "granted") {
        console.log("Notification permission already granted");
        return "granted";
      }

      // Check if permission was previously denied
      if (Notification.permission === "denied") {
        console.log("Notification permission was previously denied");

        // Show the specific message for permission denied
        const confirmReset = window.confirm(
          "Don't miss out on timely support and updates. Get notified instantly when there's something important for you. Enable notifications now?"
        );

        if (confirmReset) {
          // Show instructions based on browser
          let instructions = "To enable notifications:\n\n";

          if (navigator.userAgent.indexOf("Chrome") !== -1) {
            instructions += "1. Click the lock icon in the address bar\n";
            instructions += '2. Find "Notifications" in the permissions list\n';
            instructions += '3. Change the setting to "Allow"\n';
          } else if (navigator.userAgent.indexOf("Firefox") !== -1) {
            instructions +=
              "1. Click the site info button (lock icon) in the address bar\n";
            instructions += '2. Click on "Permissions"\n';
            instructions += '3. Find "Send Notifications" and set to "Allow"\n';
          } else if (navigator.userAgent.indexOf("Safari") !== -1) {
            instructions += "1. Click Safari in the menu bar\n";
            instructions +=
              "2. Select Preferences > Websites > Notifications\n";
            instructions += '3. Find this website and select "Allow"\n';
          } else {
            instructions += "- Open your browser settings\n";
            instructions += "- Look for Site Settings or Permissions\n";
            instructions +=
              "- Find Notifications and enable them for this site\n";
          }

          alert(instructions);
        }

        return "denied";
      }

      // If permission is not determined yet, request it with context
      if (Notification.permission === "default") {
        // Show an explanation to the user before requesting permission
        const requestPermission = window.confirm(
          "We'd like to send you notifications for important updates and messages. Would you like to enable notifications?"
        );

        if (requestPermission) {
          const permission = await Notification.requestPermission();
          console.log(
            "User responded to notification permission request:",
            permission
          );
          return permission;
        } else {
          console.log(
            "User declined to proceed with notification permission request"
          );
          return "declined";
        }
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return null;
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({ email: true, password: true });

    // Validate all fields
    let formErrors: FormErrors = {};
    if (!formData.email) {
      formErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      formErrors.email = "Invalid email address";
    }
    if (!formData.password) {
      formErrors.password = "Password is required";
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const resultAction = await dispatch(
        login({
          email: formData.email,
          password: formData.password,
        })
      ).unwrap();
      setLoading(false);
      if (resultAction.requiresOTP) {
        // --- CHANGED: Use handleOtpChoice to allow user to select OTP method ---
        handleOtpChoice(resultAction.notificationSettings);
      } else {
        const user = resultAction.user;
        // setIsAuthenticated(true);

        // Generate and store FCM token after successful login
        if (user?.uid) {
          storeFCMToken(user.uid).then((token) => {
            console.log(
              "FCM token generated and stored:",
              token ? "success" : "failed"
            );
          });
        }

        // if (user?.role === "Customer") {
        //   navigate("/user/dashboard");
        // } else if (user?.role === "Expert" || user?.role === "expert_owner") {
        //   navigate("/expert/dashboard");
        // } else if (user?.role === "supporty_admin") {
        //   navigate("/admin/dashboard");
        // } else {
        //   setErrors({ general: "Invalid role assigned to this account." });
        // }
      }
    } catch (error: any) {
      // Robustly check for Firebase 'user-disabled' error
      const errorCode = fetchError?.code || error?.data?.code;
      const isUserDisabled =
        errorCode === "auth/user-disabled" ||
        (error?.name === "FirebaseError" &&
          error?.message &&
          error.message.includes("auth/user-disabled"));
      if (isUserDisabled) {
        console.log("Detected user-disabled error, showing popup");
        setShowDeactivatedPopup(true);
      } else {
        setErrors({
          general:
            error.message ||
            error?.data?.message ||
            "Invalid email or password",
        });
      }
      setLoading(false);
    }
  };
  console.log("loafing", loading);
  React.useEffect(() => {
    if (
      fetchError &&
      (fetchError === "Firebase: Error (auth/user-disabled)." ||
        (typeof fetchError === "string" &&
          fetchError.includes("auth/user-disabled")))
    ) {
      setShowDeactivatedPopup(true);
      dispatch(clearError());
    }
  }, [fetchError, dispatch]);

  const handleOtpChoice = (options: { email?: boolean; SMS?: string }) => {
    // Only show modal if both email is true AND phoneNumber exists
    if (options.email === true && options.SMS) {
      setOtpChoiceModal({
        open: true,
        phone: options.SMS,
        email: formData.email,
        options,
      });
    } else if (options.email === true) {
      handleSendOtp("", formData.email, "EMAIL");
    } else if (options.SMS) {
      handleSendOtp(options.SMS, formData.email, "SMS");
    }
  };

  return (
    <AuthLayout>
      {/* OTP Choice Modal */}
      {otpChoiceModal.open && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md text-center">
          
            <p className="mb-6 text-gray-700">
             You have 2FA enabled on both mobile and email. Where would you like to receive your OTP?
            </p>
            <div className="flex flex-col gap-4">
               {otpChoiceModal.options?.phoneNumber && (
                <button
                  className="px-6 py-2 bg-[#F15D18] text-white rounded hover:bg-[#F15A29] focus:outline-none flex justify-center items-center"
                  style={{ maxWidth: "100%", overflow: "hidden" }}
                  onClick={() => {
                    setOtpChoiceModal({ open: false });
                    handleSendOtp(
                    otpChoiceModal?.options?.phoneNumber || "",
                      otpChoiceModal.email || "",
                      "SMS"
                    );
                  }}
                  title={otpChoiceModal.options.phoneNumber}
                >
                  <span
                    className="truncate"
                    style={{
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      verticalAlign: "bottom",
                    }}
                  >
                    Send to Mobile 
                  </span>
                </button>
              )}
              {otpChoiceModal.options?.email && (
                <button
                  className="px-6 py-2 bg-[#F15D18] text-white rounded hover:bg-[#F15A29] focus:outline-none"
                  onClick={() => {
                    setOtpChoiceModal({ open: false });
                    handleSendOtp("", otpChoiceModal.email || "", "EMAIL");
                  }}
                >
                  Send to Email 
                </button>
              )}
             
            </div>
            <button
              className="mt-6 px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none"
              onClick={() => setOtpChoiceModal({ open: false })}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Deactivated account popup */}
      {showDeactivatedPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
            <h2 className="text-xl font-bold mb-4 text-red-600">
              Account Deactivated
            </h2>
            <p className="mb-6 text-gray-700">
              Your account has been deactivated. Kindly contact Supporty admin
              for assistance.
            </p>
            <button
              className="px-6 py-2 bg-[#F15D18] text-white rounded hover:bg-[#F15A29] focus:outline-none"
              onClick={() => {
                setShowDeactivatedPopup(false);
                setErrors({});
                dispatch(clearError());
              }}
            >
              Ok
            </button>
          </div>
        </div>
      )}
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-row w-full shadow-lg rounded-lg overflow-hidden h-[100vh]">
          <div className="flex-1 flex flex-col justify-center p-8 bg-white space-y-6 items-center">
            <div className="">
              <div className="text-center ">
                <img
                  src={SupportyLogo}
                  alt="Supporty.io Logo"
                  className="mx-auto h-32 w-auto"
                />

                <h2 className="mt-1 text-[48px] nunito-extrabold text-[#0A0A0A]">
                  Sign in to Supporty.io
                </h2>
                {/* <p className="mt-2 text-[24px] text-[#696969] nunito-regular">
                  or{" "}
                  <Link
                    to="/signup"
                    className="nunito-semibold text-[#F15D18] nunito-regular hover:text-brand-primary"
                  >
                    create your account
                  </Link>
                </p> */}
              </div>

              {!showDeactivatedPopup && (errors.general || fetchError) && (
                <div className="mt-1 rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-700">
                    {errors.general || fetchError}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center mt-10">
                <form className="w-2/3" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="block text-[14px] nunito-semibold text-[#1D1D1D]"
                    >
                      Email
                    </label>
                    <div className="space-y-1">
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        onFocus={() => handleFocus("email")}
                        disabled={loading}
                        className={`w-full nunito-medium rounded-[6px] px-4 py-3 h-10 text-[#919191]  outline-none ${
                          errors.email && touched.email
                            ? "border-2 border-red-500 text-red-500"
                            : "border border-[#D9D9D9] focus:border-primary focus:ring-1"
                        }`}
                        placeholder="Enter your email address"
                      />
                      <div className="h-2">
                        {" "}
                        {/* Fixed height error container */}
                        {touched.email && errors.email && (
                          <p className="text-red-500 text-xs">{errors.email}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mt-2">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-[#1D1D1D]"
                    >
                      Password
                    </label>
                    <div className="space-y-1">
                      <div className="relative">
                        <input
                          id="password"
                          type={!showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) =>
                            handleChange("password", e.target.value)
                          }
                          disabled={loading}
                          onFocus={() => handleFocus("password")}
                          className={`w-full px-4 py-3 h-10 rounded-[6px] text-[#919191] nunito-medium outline-none ${
                            errors.password && touched.password
                              ? "border-2 border-red-500 text-red-500"
                              : "border border-[#D9D9D9] focus:border-primary focus:ring-1"
                          }`}
                          placeholder="Password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-[#919191]" />
                          ) : (
                            <Eye className="h-5 w-5 text-[#919191]" />
                          )}
                        </button>
                      </div>
                      <div className="h-2">
                        {" "}
                        {/* Fixed height error container */}
                        {touched.password && errors.password && (
                          <p className="text-red-500 text-xs">
                            {errors.password}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-10 h-[40px] w-full flex items-center nunito-bold justify-center px-4 py-3 bg-[#F15D18] text-white font-medium rounded-md hover:bg-[#F15A29] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {loading ? (
                      <>
                        <Loader className="h-5 w-5 animate-spin mr-2" />
                        Signing in...
                      </>
                    ) : (
                      <>Sign In</>
                    )}
                  </button>
                </form>
              </div>

              <p
                className="mt-10 text-center nunito-semibold text-[16px] text-base text-[#F15D18] hover:text-primary-700 cursor-pointer"
                onClick={() => navigate("/forgot-passoword")}
              >
                Forgot your password?
              </p>
            </div>
          </div>

          <div className="w-1/3 flex flex-col justify-center bg-[#FBFAF9] p-12 items-center align-middle">
            <div className="text-center">
              <img
                src={LoginSideImage}
                alt="Help Illustration"
                className="mx-auto mb-16"
              />

              <h3 className="text-[20px] text-[#919191] nunito-regular   text-start ml-[-21px] ">
                We are here to help!
              </h3>
              <ul className="mt-6 space-y-2 text-[#919191] nunito-regular text-[20px] text-start list-disc">
                <li className="mt-4 ">
                  Your trusted support, always within reach
                </li>
                <li className="mt-4">
                  Instant access to help, tips, and guidance
                </li>
                <li className="mt-4">Connect with experts anytime, anywhere</li>
                <li className="mt-4">Quick solutions for all your queries</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
